<script setup>
import { storeToRefs } from 'pinia'
import { useMessageStore } from '~/stores/messages'
import { TRACKING_CONSTANTS } from '@/utils/trackingConstants'

const props = defineProps({
    contextConfig: {
        type: Object,
        default: () => ({}),
        required: false
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fetch center list items and add placeholder property to each entry
const { data: centerList, isPlaceholderData: isCenterListPlaceholderData, suspense: centerListSuspense, isError: isCenterListError } = useFetchCenterList()

// Prefetch data on the server side
onServerPrefetch(async () => {
    await centerListSuspense() // Wait for the query to finish fetching
})

const centers = computed(() => {
    return centerList?.value?.map(item => ({
        ...item,
        placeholder: isCenterListPlaceholderData.value || isCenterListError.value
    }))
})

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.CENTER_SELECTION,
})

// Map intro title matching to sidebar context
const mapTitleObj = computed(() => {
    return {
        'profile': staticKeys.value?.account_headline_web_markdown,
        'profile-edit': staticKeys.value?.menu_personaldata_markdown,
        'profile-preferences': staticKeys.value?.menu_preferences_markdown,
        'profile-delete': staticKeys.value?.menu_deleteaccount_markdown,
        'center-details': staticKeys.value?.centerselector_mainheadline
    }
})

// Map intro copy to sidebar context
const mapCopyObj = computed(() => {
    return {
        'profile': staticKeys.value?.centerselector_text_profile_overview_web,
        'profile-edit': staticKeys.value?.centerselector_text_profile_edit_web,
        'profile-preferences': staticKeys.value?.centerselector_text_profile_preferences_web,
        'profile-delete': staticKeys.value?.centerselector_text_profile_delete_web,
        'center-details': staticKeys.value?.centerselector_text,
    }
})

// Compute title and copy based on sidebar context
const getSidebarTitle = computed(() => {
    if (!props.contextConfig?.sidebarContext) {
        return staticKeys.value?.centerselector_mainheadline
    }

    return mapTitleObj.value[props.contextConfig?.sidebarContext]
})

const getSidebarCopy = computed(() => {
    if (!props.contextConfig?.sidebarContext) {
        return staticKeys.value?.centerselector_text
    }

    return mapCopyObj.value[props.contextConfig?.sidebarContext]
})
</script>

<template>
    <SideBarWrapper class="side-bar-content-center-selector">
        <SideBarHeader>
            <SideBarMainHeadline :sidebar-main-headline-config="{ title: getSidebarTitle }" />

            <p>{{ getSidebarCopy }}</p>
        </SideBarHeader>

        <CenterList :center-list-config="{ items: centers, selectCenter: true, contextConfig: contextConfig }" />

        <DevOnly>
            <pre>{{ centers }}</pre>
        </DevOnly>
    </SideBarWrapper>
</template>

<style lang="scss">
.side-bar-content-center-selector {
    height: 100%;

    .side-bar-header {
        .sidebar-main-headline {
            margin-block-end: px2rem(30);
        }

        > p {
            text-align: center;
            margin-block: 0 px2rem(70);

            @include body2-medium;
        }
    }
}
</style>
